import React, { useEffect } from 'react'

import { Box, Div, Grid, PageTitle, PageWrapper, SectionContainer } from '@vividseats/vivid-ui-kit'
import type { GetServerSideProps, NextPage } from 'next'
import { useSetRecoilState } from 'recoil'

import MainLayout from '@/components/layouts/main-layout'
import Breadcrumbs from '@/components/shared/breadcrumbs'
import { ACCESSIBILITY_IDS, CATEGORY_IDS, NUM_VENUES_LINKS } from '@/constants'
import { AnalyticsProvider } from '@/context/analytics'
import { serverSideRead as serverSideReadCatalogAsset } from '@/context/catalog-asset'
import type { CatalogAssetDataType, CatalogAssetParams } from '@/context/catalog-asset/types'
import { articleSelector } from '@/context/cms/selectors'
import type { PerformerDataType } from '@/context/performer/types'
import { ProductionListProvider, serverSideRead as serverSideReadProductionList } from '@/context/productions-list'
import { PAGE_FILTERS } from '@/context/productions-list/constants'
import type { ProductionListData, ProductionListParams } from '@/context/productions-list/types'
import { ProductionsRowsProvider } from '@/context/productions-rows'
import { ProductionTotalsProvider, serverSideRead as serverSideReadProductionTotals } from '@/context/productions-total'
import { ProductionTotalsParams, ProductionTotalsResponse } from '@/context/productions-total/types'
import { RegionProvider, serverSideRead as serverSideReadRegion } from '@/context/region'
import { breadcrumbsSelector } from '@/context/region/selectors'
import { RelatedArtistsProvider, serverSideRead as serverSideReadRelatedArtists } from '@/context/related-artists'
import { RelatedVenuesProvider, serverSideRead as serverSideReadRelatedVenues } from '@/context/related-venues'
import { AnalyticsIds, AnalyticsSubIds } from '@/context/utils/analytics'
import { serverSideRead as serverSideReadVenues } from '@/context/venue'
import type { VenueDataType } from '@/context/venue/types'
import { useTrackPageView } from '@/hooks/use-analytics'
import { utahPerformerVenueAtom, UtahPerformerVenueState } from '@/recoil/atoms/utahPerformerVenueAtom'
import { PageType } from '@/types/app-types'
import type { Metadata, Region, Venue } from '@/types/app-types'
import { initGTM, VenuePageData } from '@/utils/analytics/ga360'
import { getSsrSearchParams } from '@/utils/query-strings'

import Article from './components/article'
import MoreVenues from './components/more-venues'
import RelatedArtists from './components/related-artists'
import VenueFilterBar from './components/venue-filter-bar/component'
import VenueProductionListings from './components/venue-production-listings'
import TopPicksBlock from './components/venue-top-picks-block/component'
import {
    NUM_VENUE_RELATED_ARTISTS,
    INITIAL_PRODUCTION_LIST_PARAMS,
    INITIAL_TOP_PICK_PRODUCTION_LIST_PARAMS,
} from './constants'
import { getMetadata, getPageTitle } from './utils'

export interface Props {
    id: number
    metadata: Metadata
    initialVenueData?: VenueDataType
    initialCatalogAssetData?: CatalogAssetDataType
    initialCatalogAssetParams?: CatalogAssetParams
    initialProductionListParams?: ProductionListParams
    initialProductionListData?: ProductionListData
    // TODO: proper typing for these 2
    initialProductionTotalsParams?: ProductionTotalsParams
    // TODO: fix this import
    initialProductionTotalsData?: ProductionTotalsResponse
    initialTopPickProductionListData?: ProductionListData
    initialRegionData?: Region
    initialMoreVenuesData?: VenueDataType
    initialSportRelatedArtistData?: PerformerDataType
    initialTheaterRelatedArtistData?: PerformerDataType
    initialConcertRelatedArtistData?: PerformerDataType
}

const VenuePage: NextPage<Props> = ({
    metadata,
    initialVenueData,
    initialProductionListParams,
    initialProductionListData,
    initialTopPickProductionListData,
    initialRegionData,
    initialMoreVenuesData,
    initialSportRelatedArtistData,
    initialTheaterRelatedArtistData,
    initialConcertRelatedArtistData,
    initialProductionTotalsData,
    initialProductionTotalsParams,
}) => {
    const venue = initialVenueData?.items[0]
    const setUtahPerformerVenue = useSetRecoilState<UtahPerformerVenueState>(utahPerformerVenueAtom)

    useEffect(() => {
        setUtahPerformerVenue({ isUtahPerformerOrVenue: venue?.state === 'UT' })
        return () => {
            setUtahPerformerVenue({ isUtahPerformerOrVenue: false })
        }
    }, [venue])

    const article = articleSelector({ article: venue?.article, lookupData: { venue, region: initialRegionData } })
    const { id, name, productionCount } = venue || {}

    useTrackPageView(PageType.Venue, venue)

    useEffect(() => {
        if (window) {
            // __APP_DATA__ is used by Google Tag Manager to report analytics events to Braze and other 3rd parties
            window.__APP_DATA__ = {
                venueId: id,
                venueName: name,
            }

            initGTM({
                eventId: id,
                eventName: name,
                productionCount: productionCount,
            } as VenuePageData)
        }

        return () => {
            if (window) {
                // Cleanup
                window.__APP_DATA__ = undefined
            }
        }
    }, [id])

    const pageTitle = getPageTitle(venue)
    const breadcrumbs = breadcrumbsSelector({ data: initialRegionData }, true)

    return (
        <AnalyticsProvider initialProps={{ id: AnalyticsIds.Venue, pageType: PageType.Venue }}>
            <ProductionListProvider
                initialParams={initialProductionListParams}
                initialProps={{ data: initialProductionListData }}
                filterMappers={PAGE_FILTERS.venuePage}
                analyticsSubIds={[AnalyticsSubIds.MainProductionList]}
            >
                <ProductionTotalsProvider
                    initialParams={initialProductionTotalsParams}
                    initialProps={{ data: initialProductionTotalsData }}
                >
                    <MainLayout white metadata={metadata}>
                        <Box id={ACCESSIBILITY_IDS.CONTENT} />
                        <PageWrapper white>
                            <PageWrapper white withGutters withMobileGutters>
                                <Box>
                                    <Box marginBottom={5} marginTop={5} desktopMarginTop={15} desktopMarginBottom={9}>
                                        <Grid>
                                            <Box paddingLeft={1} paddingRight={1}>
                                                <Breadcrumbs
                                                    links={breadcrumbs}
                                                    pageTitle={pageTitle}
                                                    pageURL={venue?.webPath}
                                                />
                                            </Box>
                                            <Box>
                                                <PageTitle altFont title={pageTitle} size="title-lg" />
                                            </Box>
                                        </Grid>
                                    </Box>
                                    <TopPicksBlock
                                        venueTopPickProductions={initialTopPickProductionListData}
                                    ></TopPicksBlock>
                                    <VenueFilterBar />
                                    <ProductionsRowsProvider>
                                        <VenueProductionListings title="Upcoming Events" />
                                    </ProductionsRowsProvider>
                                </Box>
                            </PageWrapper>
                            <PageWrapper>
                                <PageWrapper withGutters withMobileGutters>
                                    <SectionContainer>
                                        <Article article={article} />
                                    </SectionContainer>
                                </PageWrapper>
                            </PageWrapper>
                            {!!venue?.regionId && (
                                <PageWrapper withGutters white>
                                    <Box marginTop={7} marginBottom={9}>
                                        <RegionProvider
                                            initialParams={{ regionId: venue.regionId }}
                                            initialProps={{ data: initialRegionData }}
                                            initialFetchDisabled={!!initialRegionData}
                                        >
                                            <RelatedVenuesProvider
                                                initialParams={{ regionId: venue?.regionId }}
                                                initialProps={{ data: initialMoreVenuesData }}
                                                initialFetchDisabled={!!initialMoreVenuesData}
                                            >
                                                <Div marginBottom={12}>
                                                    <MoreVenues />
                                                </Div>
                                            </RelatedVenuesProvider>
                                            <RelatedArtistsProvider
                                                initialParams={{
                                                    regionId: venue.regionId,
                                                    categoryId: CATEGORY_IDS.SPORTS,
                                                }}
                                                initialProps={{ data: initialSportRelatedArtistData }}
                                                initialFetchDisabled={!!initialSportRelatedArtistData}
                                            >
                                                <RelatedArtists category="Sports" />
                                            </RelatedArtistsProvider>
                                            <RelatedArtistsProvider
                                                initialParams={{
                                                    regionId: venue.regionId,
                                                    categoryId: CATEGORY_IDS.CONCERTS,
                                                }}
                                                initialProps={{ data: initialTheaterRelatedArtistData }}
                                                initialFetchDisabled={!!initialTheaterRelatedArtistData}
                                            >
                                                <RelatedArtists category="Concerts" />
                                                <RelatedArtistsProvider
                                                    initialParams={{
                                                        regionId: venue.regionId,
                                                        categoryId: CATEGORY_IDS.THEATER,
                                                    }}
                                                    initialProps={{ data: initialConcertRelatedArtistData }}
                                                    initialFetchDisabled={!!initialConcertRelatedArtistData}
                                                >
                                                    <RelatedArtists category="Theater" />
                                                </RelatedArtistsProvider>
                                            </RelatedArtistsProvider>
                                        </RegionProvider>
                                    </Box>
                                </PageWrapper>
                            )}
                        </PageWrapper>
                    </MainLayout>
                </ProductionTotalsProvider>
            </ProductionListProvider>
        </AnalyticsProvider>
    )
}

VenuePage.displayName = 'VenuePage'

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
    const { query, res, params } = context
    const { id, slug } = query

    const searchParamsString = getSsrSearchParams({ query, params })
    const venueId = parseInt(id as string, 10)

    let venue: Venue | undefined
    let initialVenueData: VenueDataType | undefined
    let initialCatalogAssetData: CatalogAssetDataType | undefined
    let initialProductionListData: ProductionListData | undefined
    let initialTopPickProductionListData: ProductionListData | undefined
    let initialRegionData: Region | undefined
    let initialMoreVenuesData: VenueDataType | undefined
    let initialSportRelatedArtistData: PerformerDataType | undefined
    let initialTheaterRelatedArtistData: PerformerDataType | undefined
    let initialConcertRelatedArtistData: PerformerDataType | undefined
    let initialProductionTotalsData: ProductionTotalsResponse | undefined

    const initialProductionListParams = INITIAL_PRODUCTION_LIST_PARAMS(venueId, query)
    const initialTopPickProductionListParams = INITIAL_TOP_PICK_PRODUCTION_LIST_PARAMS(venueId)
    const initialCatalogAssetParams = { resource: 'VENUE' as const, resourceId: [venueId] }
    const initialProductionTotalsParams = { venueId }

    try {
        ;[
            initialVenueData,
            initialCatalogAssetData,
            initialProductionListData,
            initialTopPickProductionListData,
            initialProductionTotalsData,
        ] = await Promise.all([
            serverSideReadVenues({ id: venueId }),
            serverSideReadCatalogAsset(initialCatalogAssetParams),
            serverSideReadProductionList(initialProductionListParams),
            serverSideReadProductionList(initialTopPickProductionListParams),
            serverSideReadProductionTotals(initialProductionTotalsParams),
        ])

        venue = initialVenueData.items[0]
        const regionId = venue?.regionId

        // If there is a regionId present, also get region data, additional venues and related artists
        if (venue?.regionId) {
            ;[
                initialRegionData,
                initialMoreVenuesData,
                initialSportRelatedArtistData,
                initialTheaterRelatedArtistData,
                initialConcertRelatedArtistData,
            ] = await Promise.all([
                serverSideReadRegion({ regionId: venue.regionId }),
                serverSideReadRelatedVenues({ id: venueId, regionId, pageSize: NUM_VENUES_LINKS }),
                serverSideReadRelatedArtists({
                    regionId: venue.regionId,
                    categoryId: CATEGORY_IDS.SPORTS,
                    pageSize: NUM_VENUE_RELATED_ARTISTS,
                }),
                serverSideReadRelatedArtists({
                    regionId: venue.regionId,
                    categoryId: CATEGORY_IDS.THEATER,
                    pageSize: NUM_VENUE_RELATED_ARTISTS,
                }),
                serverSideReadRelatedArtists({
                    regionId: venue.regionId,
                    categoryId: CATEGORY_IDS.CONCERTS,
                    pageSize: NUM_VENUE_RELATED_ARTISTS,
                }),
            ])
        }

        if ((res && initialVenueData.total === 0) || !venue?.webPath) {
            res.writeHead(302, 'Not found', { Location: `/404?venueId=${venueId}` })
            res.end()
            return { props: { id: venueId, metadata: getMetadata(venue, initialCatalogAssetData?.items) } }
        }

        if (slug && venue?.webPath && `/${slug}/venue/${id}` !== venue.webPath) {
            res.writeHead(301, { Location: `${venue.webPath}${searchParamsString}` })
            res.end()
            return { props: { id: venueId, metadata: getMetadata(venue, initialCatalogAssetData?.items) } }
        }
    } catch (e) {
        console.error((e as Error).message)
    }

    return {
        props: {
            id: venueId,
            ...(initialVenueData ? { initialVenueData } : {}),
            ...(initialCatalogAssetData ? { initialCatalogAssetData } : {}),
            ...(initialProductionListData ? { initialProductionListData } : {}),
            ...(initialTopPickProductionListData ? { initialTopPickProductionListData } : {}),
            ...(initialRegionData ? { initialRegionData } : {}),
            ...(initialMoreVenuesData ? { initialMoreVenuesData } : {}),
            ...(initialSportRelatedArtistData ? { initialSportRelatedArtistData } : {}),
            ...(initialTheaterRelatedArtistData ? { initialTheaterRelatedArtistData } : {}),
            ...(initialConcertRelatedArtistData ? { initialConcertRelatedArtistData } : {}),
            ...(initialProductionTotalsData ? { initialProductionTotalsData } : {}),
            initialProductionListParams,
            initialCatalogAssetParams,
            initialProductionTotalsParams,
            metadata: getMetadata(venue, initialCatalogAssetData?.items),
        },
    }
}

export default VenuePage
